// Тарифная строка ведомости: из чего сложилось начисление. Сценарий 33.
//
// Панель открывается по нажатию на ячейку часов, тарифа или начисления — то есть на ячейку,
// которую человек править в сетке не может (правило 6). Показать её обязаны не «вместо
// отказа», а как ответ на вопрос «почему здесь именно столько».
//
// ## Ставка здесь тоже НЕ вводится числом
//
// Панель показывает пару, из которой ставка получается: сумма за период и норма часов. Ввести
// 152 руками нельзя ни здесь, ни где-либо ещё — обратный ход превращает взыскание 5 000 в
// 5 044. Поправить ставку можно только в карточке назначения, и панель говорит об этом словами,
// а не молчит.
//
// ## Округление — показ, а не расчёт
//
// В сетке стоит 152, здесь — 152,329749. Это одно и то же число: расчёт от округлённой ставки
// даёт 84 816 вместо 85 000, то есть недостачу 184 рубля на человеке при 281 человеке в
// ведомости.

import { useState } from 'react';

import { RiExternalLinkLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { PayrollRowOut, RateMode } from '@/shared/api/endpoints/payroll';
import { useAuthStore } from '@/shared/auth/store';
import { formatHours } from '@/shared/hours/format';
import { formatDecimalExact, formatKopecks, formatRateRubles } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { ErrorState, Skeleton } from '@/shared/ui/screen-state';
import * as LinkButton from '@/shared/ui/link-button';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import {
  FILTER_PERIOD,
  FILTER_SITE,
  PANEL_SHEET_TARIFF,
  VIEW_SHEETS,
  VIEW_TIMESHEET,
} from '../../route';
import { ТЕКСТ_ПРИЧИНЫ, неизвестноИз, причинаПустоты, строкаБезТарифа } from '../причины';
import { useSetRowRate, useUpdateRow } from '../queries';

import { findRow, useSheetFromScreen } from './context';

const EDIT_PERMISSION = 'core.payroll.edit';
const RATE_PERMISSION = 'core.rate.edit';

export function RowPanel({ id }: { id: string }) {
  const { grid, error, retry, nothing } = useSheetFromScreen();

  if (nothing !== null) {
    // Вечная загрузка запрещена: выключенный запрос навсегда остаётся «в ожидании», и панель
    // без этой развилки крутила бы скелет, ничего не собираясь показать.
    return <div className='text-paragraph-sm text-text-sub-600'>{nothing}</div>;
  }
  if (error !== null && error !== undefined) {
    return <ErrorState subject='строку ведомости' error={error as never} onRetry={retry} />;
  }
  if (grid === undefined) {
    return (
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-6 w-2/3 rounded-md' />
        <Skeleton className='h-40 w-full rounded-lg' />
      </div>
    );
  }

  const найдено = findRow(grid, id);
  if (найдено === null) {
    return (
      <div className='text-paragraph-sm text-text-sub-600'>
        Этой тарифной строки в открытой ведомости нет. Скорее всего, ссылка ведёт в другую ведомость
        или строку уже убрали: выберите ведомость слева и откройте строку заново.
      </div>
    );
  }

  const { row: строка, employee: человек } = найдено;
  const ведомость = grid.payroll;
  const рассчитан = человек.calculated_at !== null;
  const оклад = строка.row_type === 'salary';
  // Почему ставки нет — два разных ответа, и путать их нельзя. У строки-оклада её нет по
  // устройству: там начисление задано суммой. У почасовой строки её ещё не посчитали, и
  // написать «нет по устройству» значило бы сказать, что она не появится никогда.
  // Третий ответ появился 23.09.2026: у почасовой строки БЕЗ ТАРИФА ставка не появится ни с
  // каким расчётом — «появится вместе с расчётом» обещало то, чего не будет.
  const безТарифа = строкаБезТарифа(строка);
  const пустота = причинаПустоты(человек, строка);
  const прямая = строка.rate_mode === 'direct';
  const почемуНетСтавки = оклад
    ? 'у строки-оклада ставки нет по устройству: начисление задано суммой'
    : безТарифа
      ? 'ставки нет: в назначении не заполнен тариф, посчитать её не из чего'
      : 'ставка появится вместе с расчётом: она считается из суммы за период и нормы часов';
  const месяц = `${ведомость.period_year}-${String(ведомость.period_month).padStart(2, '0')}`;
  const адресТабеля =
    ведомость.site_id === null
      ? null
      : `/payroll/${VIEW_TIMESHEET}?f.${FILTER_PERIOD}=${месяц}&f.${FILTER_SITE}=${ведомость.site_id}`;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-md text-text-strong-950'>{человек.employee_name}</span>
        <span className='text-paragraph-sm text-text-sub-600'>
          {строка.post_name ?? строка.row_type_title} ·{' '}
          {ведомость.site_name ?? 'аппарат управления'} · {ведомость.period_title}
        </span>
      </div>

      {строка.is_excluded && (
        <p className='rounded-lg bg-bg-weak-50 p-3 text-paragraph-sm text-warning-base'>
          Строка выведена из расчёта: {строка.exclusion_basis ?? 'основание не записано'}. В итог
          ведомости её начисление не входит.
        </p>
      )}

      <FieldList>
        <FieldRow label='Тип строки' value={строка.row_type_title} />
        <FieldRow
          label='Факт часов'
          value={строка.hours === null ? null : formatHours(строка.hours)}
          missing={
            строка.hours === null
              ? 'в табеле за месяц у человека нет смен на этом объекте, а табель ещё открыт: не выходил — закройте табель, выходил — отметьте смены'
              : undefined
          }
        />
        {/* Норма и сумма за период — поля НАЗНАЧЕНИЯ человека, а не объекта. До 23.09.2026
            пустая норма отправляла к табельщику «в карточку объекта», где норматив стоял
            заполненным: финдиректор шёл туда, видел 1 116 часов и не понимал, чего от него
            хотят. У прямой ставки обоих полей нет по устройству. */}
        <FieldRow
          label='Норма часов'
          value={строка.norm_hours === null ? null : formatHours(строка.norm_hours)}
          missing={
            строка.norm_hours === null
              ? прямая || оклад
                ? 'не нужна: начисление считается без неё'
                : 'в назначении человека не задана норма часов: без неё ставку не посчитать'
              : undefined
          }
        />
        <FieldRow
          label='Сумма за период'
          value={строка.period_amount === null ? null : formatKopecks(строка.period_amount)}
          missing={
            строка.period_amount === null
              ? оклад
                ? 'начисление задано суммой оклада, без пары «сумма за период и норма»'
                : прямая
                  ? 'не нужна: ставка задана прямо, в рублях за час'
                  : 'в назначении человека не заполнена сумма за период'
              : undefined
          }
        />
        {/* Одна строка вместо двух. Раньше их было две — «Ставка в час 152.329749 ₽» и
            «Ставка на экране 152 ₽ (округлено)», — и человек читал их как две разные ставки,
            хотя это одно число. Точное значение при этом печаталось как есть, с ТОЧКОЙ:
            «152.329749» посреди продукта, где вся дробная часть отделяется запятой. */}
        <FieldRow
          label='Ставка в час'
          value={строка.rate === null ? null : `${formatRateRubles(строка.rate)}`}
          missing={строка.rate === null ? почемуНетСтавки : undefined}
          note={
            строка.rate === null
              ? undefined
              : прямая
                ? 'ставка задана прямо: начисление — часы умножить на ставку'
                : `точно ${formatDecimalExact(строка.rate)}; начисление считается от суммы за период и нормы, а не от ставки`
          }
        />
        <FieldRow
          label='Начислено по строке'
          // Ноль до расчёта — это «не знаем», ровно как и в сетке. Показав здесь «0,00», панель
          // сказала бы, что человеку за этот пост ничего не начислено.
          value={
            строка.accrued === 0 && (!рассчитан || неизвестноИз(пустота))
              ? null
              : formatKopecks(строка.accrued)
          }
          missing={
            строка.accrued === 0 && (!рассчитан || неизвестноИз(пустота))
              ? пустота !== null
                ? ТЕКСТ_ПРИЧИНЫ[пустота]
                : 'расчёта по этому человеку ещё не было: величина неизвестна, а не равна нулю'
              : undefined
          }
        />
      </FieldList>

      {/* Причина без пути к действию — жалоба. Лист тарифов открывается суженным до объекта:
          человеку нужны люди этого объекта, а не триста строк месяца. */}
      {безТарифа && ведомость.site_id !== null && (
        <LinkButton.Root variant='primary' size='small' underline asChild>
          <Link
            to={`/payroll/${VIEW_SHEETS}?f.${FILTER_PERIOD}=${месяц}&p=${PANEL_SHEET_TARIFF}:${месяц}~${ведомость.site_id}`}
          >
            Заполнить тариф людям этого объекта
          </Link>
        </LinkButton.Root>
      )}

      {прямая ? (
        <p className='text-paragraph-xs text-text-sub-600'>
          Ставка на этом объекте задана прямо, в рублях за час: начисление — факт часов умножить на
          ставку. Так заказчик и считает в своей книге на объектах с фиксированной ставкой.
        </p>
      ) : (
        <p className='text-paragraph-xs text-text-sub-600'>
          Начисление считается как «сумма за период умножить на факт часов и разделить на норму», а
          не как «округлённая ставка умножить на часы»: второе даёт 84 816 вместо 85 000, то есть
          недостачу 184 рубля на человеке.
        </p>
      )}

      <ПравкаСтроки строка={строка} можноПравить={ведомость.can_edit} />
      <ПравкаТарифа строка={строка} можноПравить={ведомость.can_edit} />

      <div className='flex flex-col gap-1 border-t border-stroke-soft-200 pt-3'>
        <span className='text-label-sm text-text-strong-950'>Откуда берутся часы</span>
        <p className='text-paragraph-sm text-text-sub-600'>
          Из табеля объекта за этот месяц. Здесь их можно поправить точечно — например, когда
          человек доработал смену после сдачи табеля, — но постоянное место ведения одно, и это
          табель.
        </p>
        {адресТабеля !== null && (
          <LinkButton.Root variant='primary' size='small' underline asChild>
            <Link to={адресТабеля}>
              Открыть табель объекта за {ведомость.period_title}
              <LinkButton.Icon as={RiExternalLinkLine} />
            </Link>
          </LinkButton.Root>
        )}
      </div>
    </div>
  );
}

/** Часы, сумма оклада и вывод строки из расчёта. Тариф правится отдельно: у него своё право. */
function ПравкаСтроки({ строка, можноПравить }: { строка: PayrollRowOut; можноПравить: boolean }) {
  const права = useAuthStore((state) => state.permissions);
  const правка = useUpdateRow();
  const оклад = строка.row_type === 'salary';
  const [часы, setЧасы] = useState(строка.hours ?? '');
  const [сумма, setСумма] = useState(строка.accrued === 0 ? '' : String(строка.accrued / 100));
  const [основание, setОснование] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);

  function сохранить(тело: Parameters<typeof правка.mutate>[0]['body']) {
    if (!can(права, EDIT_PERMISSION) || !можноПравить) {
      setЗапрет(
        !can(права, EDIT_PERMISSION)
          ? 'Править ведомость может финансовый отдел или администратор.'
          : 'Эта ведомость закрыта для правок.',
      );
      return;
    }
    setЗапрет(null);
    правка.mutate({ rowId: строка.id, body: { ...тело, version: строка.version } });
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-3'>
      <span className='text-label-sm text-text-strong-950'>Поправить строку</span>
      {оклад ? (
        <TextField
          id='sheet-row-accrued'
          label='Сумма оклада'
          hint='рублями: 85 000 либо 85000,50'
          value={сумма}
          onChange={setСумма}
        />
      ) : (
        <TextField
          id='sheet-row-hours'
          label='Факт часов'
          hint='часами: 558 либо 216,5'
          value={часы}
          onChange={setЧасы}
        />
      )}
      <Button.Root
        variant='neutral'
        mode='stroke'
        size='small'
        onClick={() => {
          if (оклад) {
            const разбор = parseRublesToKopecks(сумма);
            if (!разбор.ok) {
              setЗапрет(moneyErrorText(разбор.reason, 'сумму оклада'));
              return;
            }
            сохранить({ accrued: разбор.value });
            return;
          }
          if (!/^\d+([.,]\d{1,2})?$/.test(часы.trim())) {
            setЗапрет('Часы пишутся числом: 558 либо 216,5. Отрицательных часов не бывает.');
            return;
          }
          сохранить({ hours: часы.trim().replace(',', '.') });
        }}
      >
        {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
      </Button.Root>

      <div className='flex flex-col gap-1 pt-1'>
        <span className='text-paragraph-xs text-text-sub-600'>
          {строка.is_excluded
            ? 'Строка выведена из расчёта. Вернуть её можно тем же действием.'
            : 'Вывести строку из расчёта: её начисление перестанет попадать в итог, а сама строка останется видимой. Основание обязательно — через месяц никто не вспомнит.'}
        </span>
        {!строка.is_excluded && (
          <TextField
            id='sheet-row-exclusion'
            label='Основание вывода'
            value={основание}
            onChange={setОснование}
          />
        )}
        <Button.Root
          variant='neutral'
          mode='ghost'
          size='xsmall'
          onClick={() => {
            if (строка.is_excluded) {
              сохранить({ is_excluded: false });
              return;
            }
            if (основание.trim() === '') {
              setЗапрет('Без основания строка из расчёта не выводится: это денежное решение.');
              return;
            }
            сохранить({ is_excluded: true, exclusion_basis: основание.trim() });
          }}
        >
          {строка.is_excluded ? 'Вернуть в расчёт' : 'Вывести из расчёта'}
        </Button.Root>
      </div>

      {запрет !== null && <p className='text-paragraph-xs text-text-sub-600'>{запрет}</p>}
      {правка.error !== null && <ErrorAlert error={правка.error as never} />}
    </div>
  );
}

/**
 * Тариф строки: три режима и обязательное основание. Поля ввода САМОЙ ставки нет.
 *
 * Ставка производная: целевая 74 516 при 348 часах даёт 214,126437, а обратный ход — ввести
 * 214 — даёт 74 472, то есть молча превращает взыскание 5 000 в 5 044. Исключение одно:
 * фиксированная ставка за час, и она существует только потому, что у заказчика есть объекты со
 * ставкой «165 рублей в час».
 *
 * Тариф закрыт СВОИМ правом, а не общим правом на ведомость: тот, кто вбивает часы из табеля,
 * не должен мочь заодно понизить человеку ставку.
 */
function ПравкаТарифа({ строка, можноПравить }: { строка: PayrollRowOut; можноПравить: boolean }) {
  const права = useAuthStore((state) => state.permissions);
  const тариф = useSetRowRate();
  const [режим, setРежим] = useState<RateMode>(
    (строка.rate_mode as RateMode | null) ?? 'by_period',
  );
  const [заПериод, setЗаПериод] = useState(
    строка.period_amount === null || строка.period_amount === undefined
      ? ''
      : String(строка.period_amount / 100),
  );
  const [норма, setНорма] = useState(строка.norm_hours ?? '');
  const [целевая, setЦелевая] = useState(
    строка.target_amount === null || строка.target_amount === undefined
      ? ''
      : String(строка.target_amount / 100),
  );
  const [вЧас, setВЧас] = useState('');
  const [основание, setОснование] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);

  if (строка.row_type === 'salary') {
    return (
      <div className='flex flex-col gap-1 border-t border-stroke-soft-200 pt-3'>
        <span className='text-label-sm text-text-strong-950'>Тариф</span>
        <p className='text-paragraph-sm text-text-sub-600'>
          У строки-оклада тарифа нет по устройству: начисление задано суммой, и делить её на часы не
          на что.
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-3'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-sm text-text-strong-950'>Изменить тариф</span>
        <span className='text-paragraph-xs text-text-sub-600'>
          Тариф назначения при этом не меняется: изменение разовое, за этот месяц. Иначе человек
          недополучил бы и в следующем, и следа этого никто бы не увидел.
          {строка.rate_change_basis != null && (
            <> Сейчас в строке уже стоит понижение: {строка.rate_change_basis}.</>
          )}
        </span>
      </div>

      <SelectField
        id='sheet-rate-mode'
        label='Как задаётся'
        value={режим}
        options={[
          { value: 'by_period', label: 'Сумма за период и норма часов' },
          { value: 'by_target', label: 'Целевая сумма: сколько человек должен получить' },
          { value: 'direct', label: 'Фиксированная ставка за час' },
        ]}
        onChange={(значение) => значение !== NO_VALUE && setРежим(значение as RateMode)}
        placeholder='выберите способ'
      />

      {режим === 'by_period' && (
        <>
          <TextField
            id='sheet-rate-period'
            label='Сумма за период'
            hint='рублями: 85 000'
            value={заПериод}
            onChange={setЗаПериод}
          />
          <TextField
            id='sheet-rate-norm'
            label='Норма часов'
            hint='часами: 558'
            value={норма}
            onChange={setНорма}
          />
        </>
      )}
      {режим === 'by_target' && (
        <>
          <TextField
            id='sheet-rate-target'
            label='Целевая сумма'
            hint='сколько человек должен получить: 74 516'
            value={целевая}
            onChange={setЦелевая}
          />
          <TextField
            id='sheet-rate-norm-target'
            label='Норма часов'
            hint='часами: 372'
            value={норма}
            onChange={setНорма}
          />
        </>
      )}
      {режим === 'direct' && (
        <TextField
          id='sheet-rate-hourly'
          label='Ставка за час'
          hint='рублями за час: 165. Единственный режим, где ставка вводится числом'
          value={вЧас}
          onChange={setВЧас}
        />
      )}

      <TextField
        id='sheet-rate-basis'
        label='Основание'
        hint='обязательно: без него изменение тарифа не сохранится'
        value={основание}
        onChange={setОснование}
      />

      <Button.Root
        variant='neutral'
        mode='stroke'
        size='small'
        onClick={() => {
          if (!can(права, RATE_PERMISSION)) {
            setЗапрет(
              'Тариф меняет тот, у кого есть право на тарифы: финансовый отдел, руководство или администратор. Ввод часов такого права не даёт — это денежное решение.',
            );
            return;
          }
          if (!можноПравить) {
            setЗапрет('Эта ведомость закрыта для правок.');
            return;
          }
          if (основание.trim() === '') {
            setЗапрет('Без основания тариф не меняется: через месяц объяснить это будет нечем.');
            return;
          }
          const тело: Parameters<typeof тариф.mutate>[0]['body'] = {
            basis: основание.trim(),
            rate_mode: режим,
            version: строка.version,
          };
          if (режим === 'by_period') {
            const разбор = parseRublesToKopecks(заПериод);
            if (!разбор.ok) {
              setЗапрет(moneyErrorText(разбор.reason, 'сумму за период'));
              return;
            }
            тело.period_amount = разбор.value;
            тело.norm_hours = норма.trim().replace(',', '.');
          }
          if (режим === 'by_target') {
            const разбор = parseRublesToKopecks(целевая);
            if (!разбор.ok) {
              setЗапрет(moneyErrorText(разбор.reason, 'целевую сумму'));
              return;
            }
            тело.target_amount = разбор.value;
            тело.norm_hours = норма.trim().replace(',', '.');
          }
          if (режим === 'direct') {
            if (!/^\d+([.,]\d{1,6})?$/.test(вЧас.trim())) {
              setЗапрет('Ставка за час пишется числом: 165 либо 165,5.');
              return;
            }
            тело.hourly_rate = вЧас.trim().replace(',', '.');
          }
          setЗапрет(null);
          тариф.mutate({ rowId: строка.id, body: тело }, { onSuccess: () => setОснование('') });
        }}
      >
        {тариф.isPending ? 'Сохраняем…' : 'Изменить тариф'}
      </Button.Root>

      {запрет !== null && <p className='text-paragraph-xs text-text-sub-600'>{запрет}</p>}
      {тариф.error !== null && <ErrorAlert error={тариф.error as never} />}
    </div>
  );
}
